import type * as React from 'react'

// Epic 52 Phase 03 — Dual-Size Batch B
// (.claude/epics/52-token-and-icon-foundation/reference/ICON-NORMALISATION.md §(b)/(c))
// on `icon / Chevron Right` (16px export at node 131:9006; 12px export at node 129:5061, per
// D-ICON-1 not exported — the 16px node is the single authored source).
//
// NOTE (Phase 00 contract gap, still open): reference/ICON-NORMALISATION.md §(c) specifies a
// shared `SvgIconProps` from './types.ts'. That file has no owning task in any phase file yet
// (flagged in PHASE-00's Notes as an open gap). This component must not create `./types.ts`
// unprompted, so the prop type is inlined here rather than imported. Once `./types.ts` exists,
// this local type should be replaced with an import from it to match every other icon component.
type SvgIconProps = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
  /** Accessible name. Omit for a purely decorative icon (default: aria-hidden). */
  title?: string
  /** Rendered size in px. Defaults to 16 — the artwork's native/authored size. */
  size?: 12 | 16
}

// D-ICON-1 (2026-08-25): the 12px and 16px `Chevron Right` exports were measured
// coordinate-by-coordinate (40 coordinates compared) and found to be a uniform 4/3 scale of one
// another, ratio spread 0.000046 — equal to within float rounding. Per the owner decision this
// single component keeps the 16px artwork and viewBox (more coordinate precision than the 12px
// export) and lets the `size` prop drive only the rendered width/height attributes; the viewBox
// stays "0 0 16 16" regardless of `size`, so the 12px render is the SVG user agent uniformly
// scaling this same path data by 12/16 — mathematically identical to what the separate 12px
// artwork was (the two differed only by that same uniform scale).
export function IconChevronRight({ title, size = 16, ...rest }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path
        d="M5.47016 2.46991C5.76305 2.17702 6.23716 2.17702 6.53005 2.46991L11.5301 7.46991C11.8229 7.76281 11.8229 8.23692 11.5301 8.52981L6.53005 13.5298C6.23716 13.8227 5.76305 13.8227 5.47016 13.5298C5.17726 13.2369 5.17726 12.7628 5.47016 12.4699L9.94021 7.99986L5.47016 3.52981C5.17726 3.23692 5.17726 2.76281 5.47016 2.46991Z"
        fill="currentColor"
      />
    </svg>
  )
}
